import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./updateThemeImages.css";
import { selectAdminToken } from "../../sessionSlice";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";
import { fileWithSafeName } from "../../../utils/uploadFileName";
import { needsDirectUpload, uploadFilesDirect } from "../../../utils/adminUpload";

// Define image fields once
const IMAGE_FIELDS = [
  { key: "themeImage", label: "Thumbnail Image (200x300)", dimensions: [200, 300] },
  { key: "logo", label: "Logo Image (400,300)", dimensions: [400, 300] },
  {
    key: "background_desk",
    label: "Desktop Background Image (1920x1080)",
    dimensions: [1920, 1080],
  },
  { key: "background_mob", label: "Mobile Background (414x896)", dimensions: [414, 896] },
];

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_SIZE_MB = 10;

function UpdateThemeImages({ isOpen, onClose }) {
  // Pull theme state from Redux
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);
  const adminToken = useSelector(selectAdminToken);
  const [themeName, setThemeName] = useState("");
  const [errors, setErrors] = useState({});
  const [themeDescription, setThemeDescription] = useState("");
  
  // Local state for files and previews
  const [files, setFiles] = useState(
    IMAGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const [previews, setPreviews] = useState(
    IMAGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const fileInputs = useRef({});

  // Initialize previews when opening
  useEffect(() => {
    if (isOpen && themeData) {
      const initial = {};
      IMAGE_FIELDS.forEach(({ key }) => {
        const url = themeData[key] ? s3PublicUrl(themeData[key]) : null;
        initial[key] = url;
      });
      setPreviews(initial);
      setFiles(
        IMAGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
      );
      setThemeName(themeData.themeName ?? "");
      setThemeDescription(
        themeData.themeDescription != null ? String(themeData.themeDescription) : ""
      );
    }
  }, [isOpen, themeData]);

  if (!isOpen) return null;

  // Helper: get image dimensions
  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  // Validate file against ext, size, dims
  const validateImage = async (file, { dimensions }) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext))
      return `Only ${VALID_EXT.join(", ")} formats allowed`;
    if (file.size > MAX_SIZE_MB * 1024 * 1024)
      return `File size exceeds ${MAX_SIZE_MB}MB limit`;
    try {
      const { width, height } = await getImageDimensions(file);
      const [reqW, reqH] = dimensions;
      if (width !== reqW || height !== reqH) {
        return `Image must be ${reqW}×${reqH}px`;
      }
    } catch {
      return "Failed to validate image dimensions";
    }
    return null;
  };

  const handleFileChange = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const error = await validateImage(file, field);
    if (error) {
      await Swal.fire({ title: "Invalid Image", text: error, icon: "error" });
      e.target.value = "";
      setFiles((prev) => ({ ...prev, [field.key]: null }));
      return;
    }
    const safeFile = fileWithSafeName(file);
    setFiles((prev) => ({ ...prev, [field.key]: safeFile }));
    setPreviews((prev) => ({
      ...prev,
      [field.key]: URL.createObjectURL(safeFile),
    }));
  };

  const handleUpdate = async () => {
  // require at least one file selected
  // if (!Object.values(files).some(f => f)) {
  //   await Swal.fire({
  //     title: "Error",
  //     text: "Select at least one image to update",
  //     icon: "error",
  //   });
  //   return;
  // }


  const extraData = {
      themeName,
      themeDescription
    };

  // build FormData with only the chosen files
  const formData = new FormData();
  formData.append("themeName", currentTheme);
  formData.append("extraData", JSON.stringify(extraData));
  IMAGE_FIELDS.forEach(({ key }) => {
    if (files[key]) {
      formData.append(key, files[key]);
    }
  });

  try {
    // Images above ~4 MB cannot go through Lambda (6 MB request cap): upload them straight to S3
    // and send only their names. Smaller ones keep the original multipart request.
    const chosen = {};
    IMAGE_FIELDS.forEach(({ key }) => {
      if (files[key]) chosen[key] = files[key];
    });
    const direct = needsDirectUpload(chosen);
    const uploadedFiles = direct ? await uploadFilesDirect({ token: adminToken, files: chosen }) : null;
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`,
      direct
        ? {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({ themeName: currentTheme, extraData, uploadedFiles }),
          }
        : {
            method: "POST",
            headers: { Authorization: `Bearer ${adminToken}` },
            body: formData,
          }
    );
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.message || detail.error || "");
    }
    await Swal.fire({
      title: "Success",
      text: "Images updated",
      icon: "success",
    });
    setTimeout(function(){
      window.location.reload();
    });
    onClose();
  } catch (err) {
    await Swal.fire({
      title: "Error",
      text: err?.message || "Failed to update images",
      icon: "error",
    });
  }
};

  return (
    <>
      <div className="ut-modal-backdrop" aria-hidden="true" />
      <div
        className="ut-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ut-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ut-modal-header">
          <div className="ut-modal-header-text">
            <h2 id="ut-modal-title" className="ut-title">
              Update Theme
            </h2>
            {(themeName || themeData?.themeName) && (
              <p className="ut-theme-name">{themeName || themeData?.themeName}</p>
            )}
          </div>
          <button type="button" className="ut-modal-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="ut-modal-body">
          <div className="form-group">
            <label>Theme Name</label>
            <input
              type="text"
              value={themeName}
              onChange={(e) => {
                setThemeName(e.target.value);
                setErrors((prev) => ({ ...prev, themeName: null }));
              }}
              placeholder="Enter theme name"
            />
            {errors.themeName && <span className="error">{errors.themeName}</span>}
            <div className="code">* Max length 50 characters.</div>
          </div>
          <div className="form-group">
            <label>Theme Description</label>
            <textarea
              rows={2}
              value={themeDescription}
              onChange={(e) => {
                setThemeDescription(e.target.value.slice(0, 200));
                setErrors((prev) => ({ ...prev, themeDescription: null }));
              }}
              placeholder="Enter theme description"
              className="ut-textarea"
            />
            {errors.themeDescription && <span className="error">{errors.themeDescription}</span>}
            <div className="code">* Max length 200 characters.</div>
          </div>
          <div className="ut-image-upload-group">
            <div className="ut-file-upload-container">
              {IMAGE_FIELDS.map((field) => (
                <div className="ut-file-upload-wrapper" key={field.key}>
                  <label className="ut-label">{field.label}</label>
                  <div
                    className="ut-file-icon-container"
                    onClick={() => fileInputs.current[field.key]?.click()}
                  >
                    {previews[field.key] ? (
                      <img
                        src={previews[field.key]}
                        alt={`${field.label} preview`}
                        className="ut-thumb-preview"
                      />
                    ) : (
                      <i className="fas fa-upload ut-file-icon" />
                    )}
                  </div>
                  <input
                    ref={(el) => (fileInputs.current[field.key] = el)}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="ut-file-upload-input"
                    onChange={(e) => handleFileChange(field, e)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ut-modal-footer">
          <div className="ut-modal-actions">
            <button type="button" className="ut-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="ut-update-btn" onClick={handleUpdate}>
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateThemeImages;
